import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import styled from 'styled-components';

import { IGroupLessonRow } from 'core/hooks';

import * as S from '../../FakturowniaInvoices/FakturowniaInvoices.styled';
import * as L from '../../_detailShared/detail.styled';
import { GroupColors } from './groupColors';

interface ILessonsTableProps {
  lessons: IGroupLessonRow[];
}

const NumberCell = styled.td`
  color: ${GroupColors.textMuted};
  font-weight: 600;
`;

const LessonName = styled.div`
  font-weight: 600;
  color: ${GroupColors.textPrimary};
`;

const TimeHint = styled.div`
  font-size: 11px;
  color: ${GroupColors.textMuted};
`;

const TeacherCell = styled.td`
  color: ${GroupColors.textSecondary};
`;

const recordingTone = (
  meta: IGroupLessonRow['zoomRecordingMeta'],
): { tone: 'ok' | 'warn' | 'danger' | 'neutral'; key: string } => {
  if (!meta) return { tone: 'neutral', key: 'noRecording' };
  const status = (meta.status || '').toLowerCase();
  if (status.includes('publish') || status.includes('ai_completed'))
    return { tone: 'ok', key: 'recordingReady' };
  if (status.includes('fail'))
    return { tone: 'danger', key: 'recordingFailed' };
  if (status.includes('downloading') || status.includes('processing'))
    return { tone: 'warn', key: 'recordingProcessing' };
  return { tone: 'neutral', key: 'recordingPending' };
};

/**
 * Lessons table for ScheduleTab — one row per `GroupLessonType`,
 * sorted by `date` (passed → future). Each row shows:
 *   - lesson number + title,
 *   - date + time slot,
 *   - assigned teacher,
 *   - `finished` status pill (passed / scheduled / additional),
 *   - inline Zoom-recording chip with status colour.
 *
 * Drill-down into the lesson's journal/recording is deferred to a
 * follow-up (separate page already exists at `/zoom/lessons/:id`).
 */
export const LessonsTable = memo(({ lessons }: ILessonsTableProps) => {
  const { t } = useTranslation();

  const rows = useMemo(
    () =>
      [...lessons].sort((a, b) =>
        a.date && b.date
          ? a.date.localeCompare(b.date)
          : (a.number ?? 0) - (b.number ?? 0),
      ),
    [lessons],
  );

  if (rows.length === 0) {
    return (
      <S.Card>
        <S.CardTitle>
          {t('pages.groupDetail.schedule.lessonsTitle')}
        </S.CardTitle>
        <S.SubTitle>{t('pages.groupDetail.schedule.noLessons')}</S.SubTitle>
      </S.Card>
    );
  }

  return (
    <S.Card>
      <S.CardTitle>{t('pages.groupDetail.schedule.lessonsTitle')}</S.CardTitle>
      <L.SmallTable>
        <thead>
          <tr>
            <th style={{ width: 36 }}>#</th>
            <th>{t('pages.groupDetail.schedule.col.lesson')}</th>
            <th>{t('pages.groupDetail.schedule.col.date')}</th>
            <th>{t('pages.groupDetail.schedule.col.teacher')}</th>
            <th>{t('pages.groupDetail.schedule.col.status')}</th>
            <th>{t('pages.groupDetail.schedule.col.recording')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const rec = recordingTone(row.zoomRecordingMeta);
            return (
              <tr key={row.id}>
                <NumberCell>{row.lessonNumber ?? row.number ?? '—'}</NumberCell>
                <td>
                  <LessonName>{row.name || '—'}</LessonName>
                  {row.additional ? (
                    <L.Tag tone="warn">
                      {t('pages.groupDetail.schedule.statusAdditional')}
                    </L.Tag>
                  ) : null}
                </td>
                <td>
                  {row.date ? moment(row.date).format('DD.MM.YYYY') : '—'}
                  {row.startTime ? (
                    <TimeHint>
                      {row.startTime}
                      {row.endTime ? `–${row.endTime}` : ''}
                    </TimeHint>
                  ) : null}
                </td>
                <TeacherCell>{row.teacher?.fullName || '—'}</TeacherCell>
                <td>
                  <L.Tag tone={row.finished ? 'ok' : 'neutral'}>
                    {row.finished
                      ? t('pages.groupDetail.schedule.statusPassed')
                      : t('pages.groupDetail.schedule.statusScheduled')}
                  </L.Tag>
                </td>
                <td>
                  <L.Tag tone={rec.tone}>
                    {t(`pages.groupDetail.schedule.${rec.key}`)}
                  </L.Tag>
                </td>
              </tr>
            );
          })}
        </tbody>
      </L.SmallTable>
    </S.Card>
  );
});
